import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

interface Settings {
  id: string;
  type: string;
  tenant_id: string;
  default_model: string;
  response_language: string;
  content_filtering: 'strict' | 'moderate' | 'permissive';
  monthly_token_budget: number;
  per_user_daily_limit: number;
  cost_alert_threshold: number;
  data_retention_days: number;
  allow_export: boolean;
  pii_detection_sensitivity: 'off' | 'low' | 'medium' | 'high';
  notify_usage_limit: boolean;
  notify_cost_threshold: boolean;
  notify_new_model: boolean;
  notify_governance_violation: boolean;
  created_at: string;
  updated_at: string;
}

type SettingsUpdate = Partial<Omit<Settings, 'id' | 'type' | 'tenant_id' | 'created_at' | 'updated_at'>>;

const MODEL_OPTIONS = [
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
  { value: 'claude-3.5-sonnet', label: 'Claude 3.5 Sonnet' },
  { value: 'claude-3-opus', label: 'Claude 3 Opus' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ja', label: 'Japanese' },
];

const RETENTION_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
];

export function SettingsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState<SettingsUpdate>({});
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<{ settings: Settings }>('settings'),
  });

  const settings = data?.settings;

  // Sync form state when data loads
  useEffect(() => {
    if (settings) {
      setForm({
        default_model: settings.default_model,
        response_language: settings.response_language,
        content_filtering: settings.content_filtering,
        monthly_token_budget: settings.monthly_token_budget,
        per_user_daily_limit: settings.per_user_daily_limit,
        cost_alert_threshold: settings.cost_alert_threshold,
        data_retention_days: settings.data_retention_days,
        allow_export: settings.allow_export,
        pii_detection_sensitivity: settings.pii_detection_sensitivity,
        notify_usage_limit: settings.notify_usage_limit,
        notify_cost_threshold: settings.notify_cost_threshold,
        notify_new_model: settings.notify_new_model,
        notify_governance_violation: settings.notify_governance_violation,
      });
      setDirty(false);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (body: SettingsUpdate) =>
      api<{ settings: Settings }>('settings', { method: 'PUT', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const update = <K extends keyof SettingsUpdate>(key: K, value: SettingsUpdate[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    saveMutation.mutate(form);
  };

  const tenantName = user?.tenants?.find((t) => t.id === user.current_tenant_id)?.name ?? 'Your Organization';

  if (isLoading) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="h-8 w-48 bg-muted animate-pulse rounded mb-2" />
        <div className="h-4 w-80 bg-muted animate-pulse rounded mb-8" />
        <div className="space-y-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Settings</h1>
          <p className="text-muted-foreground">Configure AI platform behavior, limits, and preferences.</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="text-sm text-green-600 dark:text-green-400">Settings saved</span>
          )}
          <button
            onClick={handleSave}
            disabled={!dirty || saveMutation.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {saveMutation.isError && (
        <div className="mb-6 rounded-lg border border-red-500/50 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
          {saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save settings'}
        </div>
      )}

      <div className="space-y-6">
        {/* General */}
        <section className="rounded-lg border bg-card">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">General</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Organization and default AI behavior.</p>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Organization</label>
              <p className="text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">{tenantName}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Default AI Model</label>
                <select
                  value={form.default_model ?? ''}
                  onChange={(e) => update('default_model', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                >
                  {MODEL_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Response Language</label>
                <select
                  value={form.response_language ?? ''}
                  onChange={(e) => update('response_language', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                >
                  {LANGUAGE_OPTIONS.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Content Filtering</label>
                <select
                  value={form.content_filtering ?? ''}
                  onChange={(e) => update('content_filtering', e.target.value as Settings['content_filtering'])}
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                >
                  <option value="strict">Strict</option>
                  <option value="moderate">Moderate</option>
                  <option value="permissive">Permissive</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Usage Limits */}
        <section className="rounded-lg border bg-card">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">Usage Limits</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Control token budgets and spending alerts.</p>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Monthly Token Budget</label>
              <input
                type="number"
                value={form.monthly_token_budget ?? 0}
                onChange={(e) => update('monthly_token_budget', parseInt(e.target.value, 10) || 0)}
                min={0}
                step={10000}
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Total tokens across all users per month.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Per-User Daily Limit</label>
              <input
                type="number"
                value={form.per_user_daily_limit ?? 0}
                onChange={(e) => update('per_user_daily_limit', parseInt(e.target.value, 10) || 0)}
                min={0}
                step={1000}
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Max tokens each user can consume daily.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Cost Alert Threshold ($)</label>
              <input
                type="number"
                value={form.cost_alert_threshold ?? 0}
                onChange={(e) => update('cost_alert_threshold', parseFloat(e.target.value) || 0)}
                min={0}
                step={10}
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Alert when monthly spend reaches this amount.</p>
            </div>
          </div>
        </section>

        {/* Data & Privacy */}
        <section className="rounded-lg border bg-card">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">Data & Privacy</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Retention policies, exports, and PII handling.</p>
          </div>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Data Retention</label>
                <select
                  value={form.data_retention_days ?? 90}
                  onChange={(e) => update('data_retention_days', parseInt(e.target.value, 10) as 30 | 60 | 90 | 365)}
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                >
                  {RETENTION_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">How long conversation data is retained.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">PII Detection</label>
                <select
                  value={form.pii_detection_sensitivity ?? 'medium'}
                  onChange={(e) => update('pii_detection_sensitivity', e.target.value as Settings['pii_detection_sensitivity'])}
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                >
                  <option value="off">Off</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">Sensitivity of automatic PII detection in prompts.</p>
              </div>
              <div className="flex items-start pt-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.allow_export ?? true}
                    onChange={(e) => update('allow_export', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">Allow Data Export</span>
                    <p className="text-xs text-muted-foreground">Let users export conversation history.</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* Notifications */}
        <section className="rounded-lg border bg-card">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Choose which events trigger admin notifications.</p>
          </div>
          <div className="p-6 space-y-4">
            {([
              { key: 'notify_usage_limit' as const, label: 'Usage approaching limit', desc: 'Alert when token usage reaches 80% of budget.' },
              { key: 'notify_cost_threshold' as const, label: 'Cost threshold reached', desc: 'Alert when spending hits the configured dollar threshold.' },
              { key: 'notify_new_model' as const, label: 'New model available', desc: 'Notify when new AI models are added to the platform.' },
              { key: 'notify_governance_violation' as const, label: 'Governance violation', desc: 'Alert on policy violations detected by the governance engine.' },
            ]).map((n) => (
              <label key={n.key} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form[n.key] ?? false}
                  onChange={(e) => update(n.key, e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <span className="text-sm font-medium text-foreground">{n.label}</span>
                  <p className="text-xs text-muted-foreground">{n.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </section>
      </div>

      {/* Sticky bottom bar when dirty */}
      {dirty && (
        <div className="fixed bottom-0 left-0 right-0 border-t bg-card/95 backdrop-blur px-8 py-3 flex items-center justify-end gap-3 z-50">
          <span className="text-sm text-muted-foreground mr-auto">You have unsaved changes</span>
          <button
            onClick={() => {
              if (settings) {
                setForm({
                  default_model: settings.default_model,
                  response_language: settings.response_language,
                  content_filtering: settings.content_filtering,
                  monthly_token_budget: settings.monthly_token_budget,
                  per_user_daily_limit: settings.per_user_daily_limit,
                  cost_alert_threshold: settings.cost_alert_threshold,
                  data_retention_days: settings.data_retention_days,
                  allow_export: settings.allow_export,
                  pii_detection_sensitivity: settings.pii_detection_sensitivity,
                  notify_usage_limit: settings.notify_usage_limit,
                  notify_cost_threshold: settings.notify_cost_threshold,
                  notify_new_model: settings.notify_new_model,
                  notify_governance_violation: settings.notify_governance_violation,
                });
                setDirty(false);
              }
            }}
            className="px-4 py-2 rounded-md text-sm border hover:bg-muted"
          >
            Discard
          </button>
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}
